import { useCallback } from 'react'

import { ChevronRight } from '@gravity-ui/icons'
import { Button, cn, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components/common/UserAvatar'
import type { Settlement } from '@/types/settlement'
import { formatPLN } from '@/utils/currency'

interface SettlementsTableRowProps {
  settlement: Settlement;
  onSelect: (id: string) => void;
}

export function SettlementsTableRow({ settlement: s, onSelect }: SettlementsTableRowProps) {
  const { t } = useTranslation()
  const d = s.Driver

  const handleSelect = useCallback(() => {
    onSelect(d.ID)
  }, [d.ID, onSelect])

  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(d.ID)
  }, [d.ID, onSelect])

  return (
    <Table.Row key={d.ID} className="cursor-pointer">
      <Table.Cell onClick={handleSelect}>
        <div className="flex items-center gap-3">
          <UserAvatar name={d.Name} size="sm" />
          <span className="font-black truncate">{d.Name}</span>
        </div>
      </Table.Cell>
      <Table.Cell onClick={handleSelect}>{d.Phone ?? '—'}</Table.Cell>
      <Table.Cell onClick={handleSelect}>
        <div className="flex flex-col">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">
            {d.Vehicle?.Number ?? '—'}
          </span>
          {d.Vehicle && (
            <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500 truncate">
              {d.Vehicle.Model}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell onClick={handleSelect} className="text-right font-black tabular-nums">
        <span
          className={cn(
            s.Total > 0 && "text-emerald-600 dark:text-emerald-400",
            s.Total < 0 && "text-rose-600 dark:text-rose-400",
            s.Total === 0 && "text-zinc-500"
          )}
        >
          {s.Total > 0 ? '+' : ''}{formatPLN(s.Total)} PLN
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex justify-end pr-2">
          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              onClick={handleButtonClick}
              aria-label={t('common.open')}
            >
              <ChevronRight className="size-4" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.open_settlement')}</p>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
